// frontend/src/components/TransactionType/EditTransactionType.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  Receipt, Building, ArrowRightLeft, FileText, 
  Save, X, Loader2, AlertCircle, Lock, Package,
  TrendingUp, TrendingDown
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./TransactionType.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditTransactionType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType } = useAuth();

  const [formData, setFormData] = useState({ 
    transaction_type: "",
    reference_type: "",
    sign: "+",
    notes: ""
  });
  const [transactionType, setTransactionType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});

  // Icon mapping
  const getIconForType = (type) => {
    const typeLower = type?.toLowerCase() || '';
    if (typeLower.includes('opening')) return Package;
    if (typeLower.includes('purchase')) return Package;
    if (typeLower.includes('sale') && !typeLower.includes('return')) return Package;
    if (typeLower.includes('return')) return Package;
    if (typeLower.includes('transfer')) return Package;
    if (typeLower.includes('order')) return FileText;
    if (typeLower.includes('adjustment_in')) return TrendingUp;
    if (typeLower.includes('adjustment_out')) return TrendingDown;
    if (typeLower.includes('count_correction')) return Package;
    return Receipt;
  };

  useEffect(() => {
    const fetchTransactionType = async () => {
      try {
        const res = await axios.get(`${url}/api/transactiontype/${id}`);
        
        if (res.data.success) {
          const data = res.data.data;
          setTransactionType(data);
          setFormData({ 
            transaction_type: data.transaction_type || "",
            reference_type: data.reference_type || "",
            sign: data.sign || "+",
            notes: data.notes || ""
          });
        } else {
          toast.error(res.data.message || "Failed to load transaction type");
          navigate("/ViewTransactionType");
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load transaction type");
        navigate("/ViewTransactionType");
      } finally {
        setLoading(false);
      }
    };
    fetchTransactionType();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    if (name === 'transaction_type') {
      processedValue = value.toLowerCase();
    } else if (name === 'reference_type') {
      processedValue = value.toUpperCase();
    }
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: processedValue 
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) =>
    setFocused({ ...focused, [field]: formData[field] && formData[field].length > 0 });

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.transaction_type.trim()) {
      newErrors.transaction_type = "Transaction type is required";
    } else if (formData.transaction_type.length > 50) {
      newErrors.transaction_type = "Transaction type cannot exceed 50 characters";
    }
    
    if (!formData.reference_type.trim()) {
      newErrors.reference_type = "Reference type is required";
    } else if (formData.reference_type.length > 10) {
      newErrors.reference_type = "Reference type cannot exceed 10 characters";
    }
    
    if (!formData.sign) {
      newErrors.sign = "Sign is required";
    }
    
    if (!formData.notes.trim()) {
      newErrors.notes = "Notes are required";
    } else if (formData.notes.length > 500) {
      newErrors.notes = "Notes cannot exceed 500 characters";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    // Check if it's a system default (restrict certain changes)
    if (transactionType?.is_system_default) {
      const allowedUpdates = ['status', 'notes'];
      const attemptedUpdates = Object.keys(formData);
      const disallowedUpdates = attemptedUpdates.filter(key => 
        !allowedUpdates.includes(key) && formData[key] !== transactionType[key]
      );
      
      if (disallowedUpdates.length > 0) {
        toast.error(`Cannot modify system default transaction type fields: ${disallowedUpdates.join(', ')}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await axios.put(`${url}/api/transactiontype/${id}`, formData);
      
      if (res.data.success) {
        toast.success("Transaction type updated successfully");
        navigate("/ViewTransactionType");
      } else {
        toast.error(res.data.message || "Update failed");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Update failed";
      toast.error(errorMsg);
      
      if (err.response?.status === 409) {
        setErrors(prev => ({
          ...prev,
          transaction_type: "This transaction type already exists for this company"
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewTransactionType");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="transactiontype-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading transaction type details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!transactionType) {
    return (
      <div className="form-wrapper">
        <div className="transactiontype-form-container">
          <div className="error-container" style={{ textAlign: 'center', padding: '40px' }}>
            <AlertCircle size={32} color="#dc3545" />
            <p style={{ margin: '15px 0', color: '#666' }}>Transaction type not found</p>
            <button className="cancel-btn" onClick={handleCancel}>
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = transactionType.status === "active" && !transactionType.is_system_default;
  const isReadOnly = !canEdit;
  const Icon = getIconForType(transactionType.transaction_type);

  return (
    <div className="form-wrapper">
      <div className="transactiontype-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <Icon size={24} />
          <h2 className="form-title">Edit Transaction Type</h2>
          <p className="form-subtitle">
            Update transaction type information
          </p>
        </div>

        {/* Transaction Type Info */}
        {transactionType && (
          <div className="entity-info">
            <div className="info-item">
              <Building size={14} />
              <span>Company: {transactionType.company_id?.company_name} ({transactionType.company_id?.company_code})</span>
            </div>
            <div className="info-item">
              <span>ID: {transactionType.transactiontype_id}</span>
            </div>
            <div className="info-item">
              <span>Created by: {transactionType.created_by?.name || "System"} on {new Date(transactionType.created_at).toLocaleDateString()}</span>
            </div>
            {transactionType.updated_by && (
              <div className="info-item">
                <span>Last updated by: {transactionType.updated_by?.name || "System"} on {new Date(transactionType.updated_at).toLocaleDateString()}</span>
              </div>
            )}
            {transactionType.is_system_default && (
              <div className="info-item system-default-info">
                <Lock size={14} />
                <span>This is a system default transaction type. Only notes can be modified.</span>
              </div>
            )}
          </div>
        )}

        {!canEdit && (
          <div className="alert alert-warning" style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            <AlertCircle size={16} className="mr-2" />
            {transactionType.is_system_default 
              ? "This is a system default transaction type. Only notes can be modified."
              : "This transaction type is inactive and cannot be edited."
            }
          </div>
        )}

        <form className="transactiontype-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Transaction Type Name */}
            <div className={`form-field required ${focused.transaction_type || formData.transaction_type ? "filled" : ""} ${errors.transaction_type ? 'error' : ''}`}>
              <input
                type="text"
                name="transaction_type"
                value={formData.transaction_type}
                onChange={handleChange}
                onFocus={() => handleFocus("transaction_type")}
                onBlur={() => handleBlur("transaction_type")}
                placeholder="e.g., stock_adjustment_in, opening_balance, purchase"
                required
                maxLength={50}
                readOnly={transactionType.is_system_default || isReadOnly}
                style={{ 
                  backgroundColor: isReadOnly ? '#f8f9fa' : 'white',
                  textTransform: 'lowercase'
                }}
              />
              <label>Transaction Type *</label>
              {errors.transaction_type && <small className="field-error">{errors.transaction_type}</small>}
              {transactionType.is_system_default && (
                <small className="field-hint">
                  Transaction type name cannot be changed for system defaults
                </small>
              )}
            </div>

            {/* Reference Type */}
            <div className={`form-field required ${focused.reference_type || formData.reference_type ? "filled" : ""} ${errors.reference_type ? 'error' : ''}`}>
              <input
                type="text"
                name="reference_type"
                value={formData.reference_type}
                onChange={handleChange}
                onFocus={() => handleFocus("reference_type")}
                onBlur={() => handleBlur("reference_type")}
                placeholder="e.g., ADJ, OPN, GRN, INV, RTN"
                required
                maxLength={10}
                readOnly={transactionType.is_system_default || isReadOnly}
                style={{ 
                  backgroundColor: isReadOnly ? '#f8f9fa' : 'white',
                  textTransform: 'uppercase'
                }}
              />
              <label>Reference Type *</label>
              {errors.reference_type && <small className="field-error">{errors.reference_type}</small>}
              {transactionType.is_system_default && (
                <small className="field-hint">
                  Reference type cannot be changed for system defaults
                </small>
              )}
            </div>

            {/* Sign */}
            <div className={`form-field required ${errors.sign ? 'error' : ''}`}>
              <div className="select-wrapper">
                <select
                  name="sign"
                  value={formData.sign}
                  onChange={handleChange}
                  required
                  disabled={transactionType.is_system_default || isReadOnly}
                >
                  <option value="+">+ (Positive/Increase Stock)</option>
                  <option value="-">- (Negative/Decrease Stock)</option>
                  <option value="0">0 (Neutral/No Stock Effect)</option>
                </select>
                <ArrowRightLeft size={16} className="select-icon" />
              </div>
              <label>Sign *</label>
              {errors.sign && <small className="field-error">{errors.sign}</small>}
              {transactionType.is_system_default && (
                <small className="field-hint">
                  Sign cannot be changed for system defaults
                </small>
              )}
            </div>

            {/* Notes */}
            <div className={`form-field required ${focused.notes || formData.notes ? "filled" : ""} ${errors.notes ? 'error' : ''}`}>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                onFocus={() => handleFocus("notes")}
                onBlur={() => handleBlur("notes")}
                placeholder="Describe the purpose and effect of this transaction type"
                rows="3"
                maxLength={500}
                disabled={isReadOnly}
              />
              <label>Description / Notes *</label>
            </div>

            {/* Stock Effect Summary */}
            {formData.sign && (
              <div className="info-section" style={{
                background: '#e8f5e9',
                borderLeft: '4px solid #4caf50',
                padding: '15px',
                borderRadius: '4px',
                marginTop: '10px'
              }}>
                <h4 style={{ marginBottom: '8px', color: '#2e7d32' }}>
                  <Package size={16} className="mr-2" />
                  Stock Effect Summary
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ 
                    padding: '5px 10px', 
                    borderRadius: '4px', 
                    background: formData.sign === '+' ? '#c8e6c9' : formData.sign === '-' ? '#ffcdd2' : '#e0e0e0',
                    color: formData.sign === '+' ? '#2e7d32' : formData.sign === '-' ? '#c62828' : '#616161',
                    fontWeight: 'bold'
                  }}>
                    {formData.sign}
                  </div>
                  <div>
                    {formData.sign === '+' ? 'Increases stock quantity' : 
                     formData.sign === '-' ? 'Decreases stock quantity' : 
                     'No effect on stock quantity (reservation only)'}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || isReadOnly}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  Update Transaction Type
                  <Save className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTransactionType;